import { Fragment, SetStateAction, useState } from "react";
import { useRouter } from "next/navigation";
import { Dialog, Transition } from "@headlessui/react";

import { trpc } from "~/app/providers";
import { LineBucket } from "../page";

export default function AddTagModal({ line }: { line: LineBucket }) {
  const colors = [
    {
      id: "green",
      value: "green",
      bgClass: "bg-green-500",
      ringClass: "ring-green-500",
      label: "Texas Tea",
    },
    {
      id: "red",
      value: "red",
      bgClass: "bg-red-500",
      ringClass: "ring-red-500",
      label: "Fiesta Red",
    },
    {
      id: "blue",
      value: "blue",
      bgClass: "bg-blue-500",
      ringClass: "ring-blue-500",
      label: "Cobalt Blue",
    },
    {
      id: "amber",
      value: "amber",
      bgClass: "bg-amber-500",
      ringClass: "ring-amber-500",
      label: "Goldtop",
    },
  ];

  const [isOpen, setIsOpen] = useState(false);

  const [selectedColor, setSelectedColor] = useState("ColorGreen");
  const [tagName, setTagName] = useState("");

  const router = useRouter();

  const addTag = trpc.lines.addTag.useMutation({
    onSuccess: () => {
      router.refresh();
    },
  });

  const handleColorChange = (event: {
    target: { value: SetStateAction<string> };
  }) => {
    setSelectedColor(event.target.value);
  };

  function closeModal() {
    setIsOpen(false);
  }

  function openModal() {
    setIsOpen(true);
  }

  return (
    <>
      <button onClick={openModal} className="pl-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 16 16"
          fill="currentColor"
          className="size-5 text-slate-600"
        >
          <path d="M8.75 3.75a.75.75 0 0 0-1.5 0v3.5h-3.5a.75.75 0 0 0 0 1.5h3.5v3.5a.75.75 0 0 0 1.5 0v-3.5h3.5a.75.75 0 0 0 0-1.5h-3.5v-3.5Z" />
        </svg>
      </button>

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="mx-20 flex flex-col items-center">
                    <>
                      <h3 className="text-lg font-medium leading-6 text-gray-900">
                        Create new Tag
                      </h3>
                      <div className="mt-6">
                        <div>
                          <label className="relative block rounded-md border border-gray-200 shadow-sm focus-within:border-slate-300 focus-within:ring-1 focus-within:ring-slate-300">
                            <input
                              type="text"
                              onChange={(e) => setTagName(e.target.value)}
                              className="border-none bg-transparent placeholder-transparent focus:border-transparent focus:outline-none focus:ring-0"
                            />

                            <span className="pointer-events-none absolute start-2.5 top-0 -translate-y-1/2 bg-white p-0.5 text-xs text-gray-700 transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-focus:top-0 peer-focus:text-xs">
                              Tag
                            </span>
                          </label>
                        </div>
                      </div>
                    </>

                    <>
                      <div className="mt-2">
                        <fieldset className="flex flex-wrap gap-3">
                          <legend className="sr-only">Color</legend>

                          {colors.map((color) => (
                            <label
                              key={color.id}
                              htmlFor={color.id}
                              className={`block size-8 cursor-pointer rounded-full ${color.bgClass} shadow-sm ${selectedColor === color.value ? `ring-2 ${color.ringClass} ring-offset-2` : ""}`}
                            >
                              <input
                                type="radio"
                                name="ColorOption"
                                value={color.value}
                                id={color.id}
                                className="sr-only"
                                checked={selectedColor === color.value}
                                onChange={handleColorChange}
                              />
                              <span className="sr-only">{color.label}</span>
                            </label>
                          ))}
                        </fieldset>
                      </div>
                    </>

                    <>
                      <div className="mt-12">
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                          onClick={() => {
                            addTag.mutate({
                              bucketId: line.bucketId,
                              tagColor: selectedColor,
                              tagText: tagName,
                            });
                            closeModal();
                          }}
                        >
                          Add Tag
                        </button>
                      </div>
                    </>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
